import axios from 'axios';

import { type UseUtxo } from '@/types';

export class UniversalCache<T> {
  private storageKey: string;

  constructor(storageKey: string) {
    this.storageKey = storageKey;
  }

  get(key: string): { data: T; timestamp: number } | null {
    const cachedData = sessionStorage.getItem(this.storageKey);
    if (!cachedData) return null;

    const parsedData = JSON.parse(cachedData) as {
      [key: string]: { data: T; timestamp: number };
    };

    return parsedData[key] || null;
  }

  set(key: string, data: T): void {
    const now = Date.now();
    const cachedData = sessionStorage.getItem(this.storageKey);
    const parsedData = cachedData ? JSON.parse(cachedData) : {};

    parsedData[key] = { data, timestamp: now };
    sessionStorage.setItem(this.storageKey, JSON.stringify(parsedData));
  }

  has(key: string, cacheDuration: number): boolean {
    const item = this.get(key);
    return !!(item && Date.now() - item.timestamp < cacheDuration);
  }
}

export type ResultUtxo = {
  status: { confirmed: boolean };
  txid: string;
  value: number;
  vout: number;
};

export type ResultTx = {
  txid: string;
  version: number;
  locktime: number;
  vin: {
    txid: string;
    vout: number;
    prevout: BtcTxDataOutput;
    is_coinbase?: boolean;
  }[];
  vout: BtcTxDataOutput[];
  size: number;
  weight: number;
  fee: number;
  status: {
    confirmed: boolean;
    block_height?: number;
    block_hash?: string;
    block_time?: number;
  };
};

export const getTxInfo = async (txid: string, apiEndpoint: string): Promise<ResultTx> => {
  const res = await axios.get<ResultTx>(`${apiEndpoint}/tx/${txid}`);

  return res.data;
};

export const getUtxos = async (wallet: string, apiEndpoint: string): Promise<Array<ResultUtxo>> => {
  const res = await axios.get<Array<ResultUtxo>>(`${apiEndpoint}/address/${wallet}/utxo`);

  return res.data;
};

export const getMempoolTxsSpendingUtxo = async (
  txId: string,
  vout: string | number,
  apiEndpoint: string
): Promise<Array<string>> => {
  try {
    const res = await axios.get<string[]>(`${apiEndpoint}/${txId}:${vout}`);

    return res.data;
  } catch {
    return [];
  }
};

export const getSmallestUnusedUtxo = async (wallet: string, blockedUtxosInUse: UseUtxo[], apiEndpoint: string) => {
  let selectedUtxo;
  const utxos = await getUtxos(wallet, apiEndpoint);

  const checkIsMatchUtxo = (walletUtxo: ResultUtxo) => (utxoInUse: UseUtxo) => {
    return utxoInUse.l1TxId === walletUtxo.txid && utxoInUse.vout.toString() === walletUtxo.vout.toString();
  };

  for (const utxo of utxos) {
    if (blockedUtxosInUse.find(checkIsMatchUtxo(utxo))) continue;
    if (utxo.status.confirmed && (selectedUtxo?.value || Number.MAX_SAFE_INTEGER) > utxo.value) {
      selectedUtxo = utxo;
    }
  }

  // need some rules for work with 'utxo not found'
  if (!selectedUtxo) {
    throw new Error('UTXO not found');
  }

  return selectedUtxo;
};

type BtcTxDataOutput = {
  scriptpubkey: string;
  scriptpubkey_asm: string;
  scriptpubkey_type: string;
  scriptpubkey_address: string;
  value: number;
};

export type BtcTxData = {
  txid: string;
  sigops: number;
  weight: number;
  fee: number;
  vout: BtcTxDataOutput[];
  vin: {
    txid: string;
    vout: number;
    sequence: number;
    prevout: BtcTxDataOutput;
  }[];
  status: BtcTxStatus;
};

const TX_DATA_STORAGE = 'txDataStorage';
const TX_DATA_CACHE_DURATION = 12 * 1000; // 12 seconds
const txUniversalCache = new UniversalCache<BtcTxData>(TX_DATA_STORAGE);

export const getTxData = async (txId: string, apiEndpoint: string): Promise<BtcTxData> => {
  const cachedEntry = txUniversalCache.get(txId);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < TX_DATA_CACHE_DURATION) {
    return cachedEntry.data;
  }
  const tx = (await axios.get<BtcTxData>(`${apiEndpoint}/tx/${txId}`)).data;
  txUniversalCache.set(txId, tx);
  return tx;
};

export type BtcTxStatus = {
  confirmed: boolean;
  block_height: number;
  block_hash: string;
  block_time: number;
};

type BtcTxOutputData = {
  spent: boolean;
  txid: string;
  vin: number;
  status: BtcTxStatus;
};

const TX_OUTPUT_DATA_STORAGE = 'txOutputDataStorage';
const TX_OUTPUT_CACHE_DURATION = 12 * 1000; // 12 seconds
const txOutputUniversalCache = new UniversalCache<BtcTxOutputData>(TX_OUTPUT_DATA_STORAGE);

export const getTxOutputDataByVout = async (
  txId: string,
  vout: number,
  apiEndpoint: string
): Promise<BtcTxOutputData> => {
  const cacheKey = `${txId}:${vout}`;
  const cachedEntry = txOutputUniversalCache.get(cacheKey);

  if (cachedEntry) {
    // If the UTXO is marked as spent, always return the cached entry.
    if (cachedEntry.data.spent) {
      return cachedEntry.data;
    }
    // Otherwise, return the cached entry if it's within the valid duration.
    if (Date.now() - cachedEntry.timestamp < TX_OUTPUT_CACHE_DURATION) {
      return cachedEntry.data;
    }
  }

  const utxo = (await axios.get<BtcTxOutputData>(`${apiEndpoint}/tx/${txId}/outspend/${vout}`)).data;

  txOutputUniversalCache.set(cacheKey, utxo);
  return utxo;
};

export type BtcCpfpTxData = {
  txid: string;
  fee: number;
  weight: number;
};

export type BtcCpfpChain = {
  ancestors?: BtcCpfpTxData[] | null;
  bestDescendant?: BtcCpfpTxData | null;
  descendants?: BtcCpfpTxData[] | null;
  effectiveFeePerVsize?: number;
  sigops?: number;
  fee?: number;
  adjustedVsize?: number;
};

const CPFP_CHAIN_DATA_STORAGE = 'btcCpfpChainDataStorage';
const CPFP_CHAIN_CACHE_DURATION = 12 * 1000; // 12 seconds
const btcCpfpChainCache = new UniversalCache<BtcCpfpChain>(CPFP_CHAIN_DATA_STORAGE);

export const getBtcCpfpChain = async (txid: string, apiEndpoint: string): Promise<BtcCpfpChain> => {
  const cachedEntry = btcCpfpChainCache.get(txid);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < CPFP_CHAIN_CACHE_DURATION) {
    return cachedEntry.data;
  }

  try {
    const url = `${apiEndpoint}/v1/cpfp/${txid}`;
    const { data } = await axios.get<BtcCpfpChain>(url);
    btcCpfpChainCache.set(txid, data);

    return data;
  } catch {
    const data: BtcCpfpChain = {};
    btcCpfpChainCache.set(txid, data);

    return data;
  }
};

const TX_STATUS_STORAGE = 'txStatusStorage';
const TX_STATUS_CACHE_DURATION = 12 * 1000; // 12 seconds
const txStatusCache = new UniversalCache<BtcTxStatus>(TX_STATUS_STORAGE);

export const getTxStatus = async (txid: string, apiEndpoint: string): Promise<BtcTxStatus> => {
  const cachedEntry = txStatusCache.get(txid);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < TX_STATUS_CACHE_DURATION) {
    return cachedEntry.data;
  }
  const url = `${apiEndpoint}/tx/${txid}/status`;
  const { data } = await axios.get<BtcTxStatus>(url);
  txStatusCache.set(txid, data);
  return data;
};

const TX_OUTSPENDS_STORAGE = 'txDataOutspendsStorage';
const TX_OUTSPENDS_CACHE_DURATION = 12 * 1000; // 12 seconds
const txOutspendsCache = new UniversalCache<BtcTxOutputData[]>(TX_OUTSPENDS_STORAGE);

export const getTxOutspends = async (txId: string, apiEndpoint: string): Promise<BtcTxOutputData[]> => {
  const cachedEntry = txOutspendsCache.get(txId);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < TX_OUTSPENDS_CACHE_DURATION) {
    return cachedEntry.data;
  }
  const { data } = await axios.get<BtcTxOutputData[]>(`${apiEndpoint}/tx/${txId}/outspends`);
  txOutspendsCache.set(txId, data);
  return data;
};

export const getLatestCpfpDescendant = async (txid: string, apiEndpoint: string): Promise<string> => {
  const currentStatus = await getTxStatus(txid, apiEndpoint);

  if (currentStatus.confirmed) {
    throw new Error(`Transaction with txId ${txid} is already confirmed.`);
  }

  const { descendants } = await getBtcCpfpChain(txid, apiEndpoint);

  if (!descendants?.length) {
    return txid;
  }

  let latestChildTxStatus;
  for (const descendant of descendants) {
    const descendantTxStatus = await getTxStatus(descendant.txid, apiEndpoint);

    if (!latestChildTxStatus || descendantTxStatus.block_height > currentStatus.block_height) {
      latestChildTxStatus = { txid: descendant.txid, ...descendantTxStatus };
    }
  }

  return latestChildTxStatus?.txid || txid;
};

const TX_DATA_RAW = 'txHexStorage';
const TX_HEX_CACHE_DURATION = 12 * 1000; // 12 seconds
const txHexCache = new UniversalCache<string>(TX_DATA_RAW);

export const getTxHex = async (txid: string, apiEndpoint: string): Promise<string> => {
  const cachedEntry = txHexCache.get(txid);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < TX_HEX_CACHE_DURATION) {
    return cachedEntry.data;
  }
  const { data } = await axios.get<string>(`${apiEndpoint}/tx/${txid}/hex`);
  txHexCache.set(txid, data);
  return data;
};

export interface RbfReplacementTimelineTransaction {
  txid: string;
  fee: number;
  vsize: number;
  value: number;
  rate: number;
  rbf: boolean;
  fullRbf?: boolean;
  mined?: boolean;
}

export interface RbfReplacementTimeline {
  tx: RbfReplacementTimelineTransaction;
  time: number;
  interval?: number;
  fullRbf: boolean;
  replaces: RbfReplacementTimeline[];
}

export interface RbfReplacementData {
  replacements: RbfReplacementTimeline | null;
  replaces: string[] | null;
}

const TX_RBF_TIMELINE = 'txRbfTimelineStorage'; // 12 seconds
const TX_RBF_CACHE_DURATION = 12 * 1000; // 12 seconds
const txRbfCache = new UniversalCache<RbfReplacementData>(TX_RBF_TIMELINE);

export const getTxRbfTimeline = async (txId: string, apiEndpoint: string): Promise<RbfReplacementData> => {
  const cachedEntry = txRbfCache.get(txId);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < TX_RBF_CACHE_DURATION) {
    return cachedEntry.data;
  }
  const { data } = await axios.get<RbfReplacementData>(`${apiEndpoint}/tx/${txId}/rbf`);
  txRbfCache.set(txId, data);
  return data;
};
